import { ENDPOINTS, getFullUrl } from "../config";
import {
  CreateOfflineOrderPayload,
  CreateOfflineReturnPayload,
  UpdateOfflineOrderPayload,
} from "./types";

export class OfflineItemService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }
  static getRole(): string | null {
    return localStorage.getItem("auth_user");
  }

  static async getClinicDetailsById(clinicId: string) {
    if (!clinicId) {
      throw new Error("Clinic ID is required");
    }

    const url = `${getFullUrl(
      ENDPOINTS.OFFLINE_ORDER.GET_CLINIC_DETAILS_BY_ID
    )}?clinicIds=${clinicId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to fetch clinic details:", error);
      throw error;
    }
  }

  static async createOfflineOrder(payload: CreateOfflineOrderPayload) {
    const response = await fetch(
      getFullUrl(ENDPOINTS.OFFLINE_ORDER.CREATE_NEW_ORDER),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(payload),
      }
    );
    return response.json();
  }

  static async getOfflineOrderList(params: {
    page?: number;
    pageSize?: number;
    clinicId?: string;
    searchBy?: string;
    searchValue?: string;
    date?: string;
    
  }) {
    try {
      const queryParams = new URLSearchParams();

      // Explicitly convert to strings only after ensuring they're numbers
      if (params.page !== undefined)
        queryParams.append("page", String(Number(params.page)));
      if (params.pageSize !== undefined)
        queryParams.append("pageSize", String(Number(params.pageSize)));
      if (params.clinicId) queryParams.append("clinicId", params.clinicId);
      if((params.searchBy && params.searchBy !== "") && (params.searchValue && params.searchValue !== "")){
        queryParams.append("searchBy", params.searchBy);
        queryParams.append("searchValue", params.searchValue);
      }
      if(params.date && params.date !== ""){
        queryParams.append("date", params.date);
      }

      const url = `${getFullUrl(
        ENDPOINTS.OFFLINE_ORDER.GET_OFFLINE_ORDERS
      )}?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${this.getToken()}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching offline orders:", error);
      throw error;
    }
  }

  static async getOfflineOrderDetailsByOrderId(orderId: string) {
    try {
      const url = `${getFullUrl(
        ENDPOINTS.OFFLINE_ORDER.GET_OFFLINE_ORDER_DETAILS_BY_ORDER_ID
      )}/${orderId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${this.getToken()}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching offline orders:", error);
      throw error;
    }
  }

  static checkReturnItemStatus(orderId: string) {
    const url = `${getFullUrl(
      ENDPOINTS.OFFLINE_ORDER.CHECK_RETURN_ITEM_STATUS
    )}?orderId=${encodeURIComponent(orderId)}`;

    return fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  }

  static async returnOfflineOrder(payload: CreateOfflineReturnPayload) {
    const response = await fetch(
      getFullUrl(ENDPOINTS.OFFLINE_ORDER.RETURN_OFFLINE_ORDER),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(payload),
      }
    );
    return response.json();
  }

  static async getClinicGSTNByPincode(pincode: string) {
    const url = `${getFullUrl(
      ENDPOINTS.OFFLINE_ORDER.GET_CLINIC_GSTN_BY_PINCODE
    )}?pincode=${encodeURIComponent(pincode)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });

    return await response.json();
  }

  static async getPatientByNumber(mobNumber: string) {
    const url = `${getFullUrl(
      ENDPOINTS.PATIENT.GETBYNUMBER
    )}?mobileNum=${encodeURIComponent(mobNumber)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });

    return await response.json();
  }

  static async updateOfflineOrder(
    orderId: string | number,
    payload: UpdateOfflineOrderPayload
  ) {
    try {
      const url = `${getFullUrl(
        ENDPOINTS.OFFLINE_ORDER.UPDATE_OFFLINE_ORDER
      )}/${orderId}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data.message || `Error: ${response.status} ${response.statusText}`
        );
      }

      return data;
    } catch (error) {
      console.error("Failed to update offline order:", error);
      throw error;
    }
  }
}
